import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { customerAuth } from "../lib/customerAuth";
import { useAuth } from "../lib/auth";
import { CheckCircle, WarningCircle, ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

export default function CustomerActivate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [mode, setMode] = useState("google");   // "google" | "password"
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer"; meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => { try { document.head.removeChild(meta); } catch { /* noop */ } };
  }, []);

  useEffect(() => {
    if (!token) return;
    api.get(`/portal/activate/preview?token=${encodeURIComponent(token)}`)
      .then(r => setPreview(r.data))
      .catch(e => setPreviewErr(e?.response?.data?.detail || "Invalid or expired activation link."));
  }, [token]);

  const activateGoogle = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post("/portal/activate", { token });
      window.location.href = "/portal";
    } catch (e) { setErr(e?.response?.data?.detail || "Activation failed."); }
    finally { setBusy(false); }
  };

  const activatePassword = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (pw !== pw2) { setErr("Passwords do not match."); return; }
    if (pw.length < 12) { setErr("Password must be at least 12 characters."); return; }
    setBusy(true);
    try {
      await customerAuth.activatePassword(token, pw);
      setPw(""); setPw2("");
      window.location.href = "/portal";
    } catch (ex) {
      setPw(""); setPw2("");
      setErr(ex.message || "Activation failed.");
    } finally { setBusy(false); }
  };

  if (!token) return <Center><ErrorCard message="This activation link is missing a token." /></Center>;
  if (previewErr) return <Center><ErrorCard message={previewErr} /></Center>;

  return (
    <Center>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden" data-testid="portal-activate-card">
        <div className="px-6 py-5 border-b border-slate-100">
          <div className="text-xl font-semibold text-slate-900">Activate your access</div>
          <div className="text-xs text-slate-500 mt-1">Choose how you'd like to sign in going forward.</div>
        </div>
        <div className="px-6 py-5 space-y-4">
          {preview && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
              <div><span className="text-slate-500 text-xs">Invited email:</span> <span className="font-mono" data-testid="portal-activate-email">{preview.email}</span></div>
              <div><span className="text-slate-500 text-xs">Company:</span> <span>{preview.company_name || "—"}</span></div>
              <div><span className="text-slate-500 text-xs">Expires:</span> <span className="font-mono text-xs">{preview.expires_at ? new Date(preview.expires_at).toLocaleString() : "—"}</span></div>
            </div>
          )}

          <div className="flex gap-2" role="tablist">
            <button
              role="tab"
              onClick={() => setMode("google")}
              data-testid="portal-activate-tab-google"
              className={`flex-1 px-3 py-2 rounded-lg text-sm border ${mode === "google" ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-700 bg-white"}`}
            >Continue with Google</button>
            <button
              role="tab"
              onClick={() => setMode("password")}
              data-testid="portal-activate-tab-password"
              className={`flex-1 px-3 py-2 rounded-lg text-sm border ${mode === "password" ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-700 bg-white"}`}
            >Create Password</button>
          </div>

          {mode === "google" ? (
            loading ? (
              <div className="text-sm text-slate-500">Loading your Google session…</div>
            ) : !user ? (
              <a
                href={`/customer/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`}
                data-testid="portal-activate-signin-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
              >
                Sign in with Google <ArrowRight size={16} weight="bold" />
              </a>
            ) : normalizedEmail(user.email) !== normalizedEmail(preview?.email) ? (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">
                This link belongs to <span className="font-mono">{preview?.email}</span>, but you're signed in as <span className="font-mono">{user.email}</span>. Sign out first.
              </div>
            ) : (
              <button
                onClick={activateGoogle}
                disabled={busy}
                data-testid="portal-activate-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
              >
                <CheckCircle size={16} weight="bold" /> {busy ? "Activating…" : "Activate my access"}
              </button>
            )
          ) : (
            <form onSubmit={activatePassword} className="space-y-3" data-testid="portal-activate-password-form">
              <PwField id="pa-pw1" label="New password" val={pw} set={setPw} show={show} setShow={setShow} testid="portal-activate-new" />
              <PwField id="pa-pw2" label="Confirm password" val={pw2} set={setPw2} show={show} setShow={setShow} testid="portal-activate-confirm" />
              <button type="submit" disabled={busy}
                      data-testid="portal-activate-password-submit"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">
                {busy ? "Activating…" : "Activate account"}
              </button>
            </form>
          )}
          {err && <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800" data-testid="portal-activate-error">{err}</div>}
        </div>
      </div>
    </Center>
  );
}
function PwField({ id, label, val, set, show, setShow, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-700 block mb-1" htmlFor={id}>{label}</label>
      <div className="relative">
        <input id={id} type={show ? "text" : "password"} autoComplete="new-password"
               minLength={12} maxLength={256} required value={val} onChange={(e) => set(e.target.value)}
               data-testid={testid}
               className="w-full rounded-lg border border-slate-300 text-sm px-3 py-2 pr-9 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        <button type="button" onClick={() => setShow(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
                aria-label={show ? "Hide password" : "Show password"}>
          {show ? <EyeSlash size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );
}
function Center({ children }) { return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="portal-activate-page">{children}</div>; }
function ErrorCard({ message }) {
  return (
    <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 p-6" data-testid="portal-activate-error">
      <div className="flex items-start gap-3 text-rose-700">
        <WarningCircle size={22} weight="bold" />
        <div><div className="font-semibold">Activation link problem</div>
             <div className="text-sm mt-1 text-slate-700">{message}</div></div>
      </div>
    </div>
  );
}
function normalizedEmail(s) { return (s || "").trim().toLowerCase(); }
